"use client";

import { GenuiSectionIcon } from "./GenuiSectionIcon";
import type { InsightsGenuiBar as GenuiBarSpec } from "./parse-genui";
import { cn } from "@/shared/lib/utils/cn";

function looksMonetary(field: string): boolean {
  return /amount|revenue|pipeline|value|usd|arr|mrr|cost|spend/i.test(field);
}

function formatBarValue(value: number, monetary: boolean): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  let body: string;
  if (abs >= 1_000_000) {
    body = `${(abs / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  } else if (abs >= 10_000) {
    body = `${(abs / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  } else if (abs >= 1_000) {
    body = abs.toLocaleString(undefined, { maximumFractionDigits: 0 });
  } else {
    body = abs.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return monetary ? `${sign}$${body}` : `${sign}${body}`;
}

/**
 * Horizontal bars for GenUI `{ type: "bar", x, y, data }` inside <render>.
 * Caller: InsightsAnswer. Title metrics match InsightsMessageText h2 (16px semibold).
 */
export function InsightsGenuiBar({
  spec,
  icon,
  live = false,
}: {
  spec: GenuiBarSpec;
  icon?: string;
  live?: boolean;
}) {
  const max = Math.max(...spec.data.map((point) => Math.abs(point.value)), 1);
  const monetary = looksMonetary(spec.y);

  return (
    <figure className={cn("my-0 w-full min-w-0 bg-background", live && "bg-primary/[0.02]")}>
      {spec.title ? (
        <figcaption className="flex items-center gap-2.5 border-b border-border/50 bg-muted/30 px-3.5 py-2.5 text-[16px] font-semibold leading-snug text-foreground">
          <GenuiSectionIcon icon={icon} hint={spec.title} kind="bar" />
          <span className="min-w-0">{spec.title}</span>
        </figcaption>
      ) : live ? (
        <figcaption className="border-b border-border/50 bg-muted/30 px-3.5 py-2.5">
          <span className="inline-block h-3.5 w-44 animate-pulse rounded bg-muted" />
        </figcaption>
      ) : null}
      <div className="space-y-3 px-3 py-3">
        {spec.data.map((point) => {
          const width = Math.max(2, Math.round((Math.abs(point.value) / max) * 100));
          return (
            <div
              key={`${point.label}-${point.value}`}
              className="grid grid-cols-[minmax(5.5rem,9rem)_1fr_auto] items-center gap-2"
            >
              <span className="truncate text-[14px] text-foreground/90" title={point.label}>
                {point.label}
              </span>
              <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary/80 transition-[width] duration-300"
                  style={{ width: `${width}%` }}
                />
              </div>
              <span className="whitespace-nowrap tabular-nums text-[14px] font-medium text-foreground">
                {formatBarValue(point.value, monetary)}
              </span>
            </div>
          );
        })}
        {live ? (
          <div className="grid grid-cols-[minmax(5.5rem,9rem)_1fr_auto] items-center gap-2">
            <span className="inline-block h-3 w-16 animate-pulse rounded bg-muted" />
            <div className="h-2.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full w-1/3 animate-pulse rounded-full bg-muted-foreground/20" />
            </div>
            <span className="inline-block h-3 w-10 animate-pulse rounded bg-muted" />
          </div>
        ) : null}
        {!live && spec.data.length === 0 ? (
          <p className="text-[14px] text-muted-foreground">No data</p>
        ) : null}
      </div>
    </figure>
  );
}
